import React from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { LearnerWhatsNew } from './learnerWhatsNew';
import WhatsNewMessage from './WhatsNewMessage';
import DosenButton from '../ui/Buttons/DosenButton';

/**
 * "What's new in <program>" — shown once per published version to a learner
 * (useProgramWhatsNew). This learner's own notice: the new lessons, the
 * updated lessons and what changed in them, then the actions they need to
 * complete. Same dark treatment as the Elliott lesson page.
 */

const TEXT = '#FDFDFD';
const TEXT_2 = '#B6B6B6';
const LINE = '#2A2A2A';
const BRANDY = '#F79420';
const FONT = 'Inter, sans-serif';

interface Props {
  open: boolean;
  programName?: string;
  whatsNew: LearnerWhatsNew | null;
  onClose: () => void;
}

const WhatsNewModal: React.FC<Props> = ({ open, programName, whatsNew, onClose }) => {
  const hasLessons = Boolean(whatsNew && (whatsNew.newLessons.length || whatsNew.updatedLessons.length));
  return (
    <Dialog
      open={open && Boolean(whatsNew)}
      onClose={onClose}
      aria-labelledby="whats-new-title"
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(10px)' } } }}
      PaperProps={{
        sx: {
          width: 560,
          maxWidth: '95vw',
          bgcolor: '#0F0F0F',
          color: TEXT,
          borderRadius: '8px',
          outline: '1px solid #444444',
          outlineOffset: '-1px',
          boxShadow: 'none'
        }
      }}
    >
      <Box sx={{ px: '24px', py: '20px', borderBottom: `1px solid ${LINE}`, position: 'relative' }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: BRANDY, letterSpacing: '0.06em' }}>
          PROGRAM UPDATE
        </Typography>
        <Typography
          id="whats-new-title"
          sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 700, color: TEXT, mt: '4px' }}
        >
          What’s new{programName ? ` in ${programName}` : ''}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ p: 0.5, position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)' }}
        >
          <CloseIcon sx={{ fontSize: 20, color: TEXT }} />
        </IconButton>
      </Box>
      <Box sx={{ px: '24px', py: '20px' }}>
        {whatsNew && hasLessons ? (
          <WhatsNewMessage whatsNew={whatsNew} />
        ) : (
          <Typography sx={{ fontFamily: FONT, fontSize: 14, lineHeight: '22px', color: TEXT_2 }}>
            This program was updated. Lessons you’ve completed stay completed.
          </Typography>
        )}
      </Box>
      <Box sx={{ px: '24px', py: '16px', borderTop: `1px solid ${LINE}`, display: 'flex', justifyContent: 'flex-end' }}>
        <DosenButton onClick={onClose}>Close</DosenButton>
      </Box>
    </Dialog>
  );
};

export default WhatsNewModal;
